import React from 'react';
import { Route, Routes, Outlet } from 'react-router-dom';
import { useAuth } from './auth/AuthContext';
import { userHasRole } from './utils/helper';
import { docEditDefaultValues, UserRoles } from './constants';

import DocAddPage from './pages/DocAdd';
import DocApiDownload from './doc-api/DocApiDownload';
import DocApiPreview from './doc-api/DocApiPreview';
import DocApiZip from './doc-api/DocApiZip';
import DocEditPage from './pages/DocEdit';
import DocSearchPage from './doc-search/DocSearchPage';
import DocViewPage from './doc-view/DocViewPage';
import MetadataExport from './pages/metadata-export/MetadataExport';
import FormList from './pages/FormList';
import LinkLoader from './pages/Link';
import NotFound from './pages/NotFound';
import Dictionary from './pages/Admin/Dictionary';
import ArchiveOrgHierarchy from './pages/Admin/ArchiveOrgHierarchy';
import AddOrgHierarchy from './pages/Admin/AddOrgHierarchy';
import AuthLogout from './auth/AuthLogout';
import AuthMain from './auth/AuthMain';
import AuthTokenRefresh from './auth/AuthTokenRefresh';
import { LoginCallback } from '@okta/okta-react';
import OktaErrorHandler from './auth/OktaErrorHandler';
import SecuredRoute from './components/SecuredRoute';

const CustomLoginCallback = () => <LoginCallback errorComponent={OktaErrorHandler} />;

export const AppRoutes = () => {
  const { user, isLoading, isAuthenticated } = useAuth();

  if (isLoading) {
    return (
      <div className="wrapper container external">
        <div className="content-box content-box-600">
          <h3>Checking authentication</h3>
          <p>Please wait...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/implicit/callback" element={<CustomLoginCallback />} />
        <Route path="/auth-refresh" element={<AuthTokenRefresh />} />
        <Route path="/logout" element={<AuthLogout />} />
        <Route path="*" element={<AuthMain />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route
        path="/search"
        element={
          <SecuredRoute>
            <DocSearchPage />
          </SecuredRoute>
        }
      />
      <Route
        path="/view/:id"
        element={
          <SecuredRoute>
            <DocViewPage />
          </SecuredRoute>
        }
      />
      <Route
        path="/forms/add"
        element={
          <SecuredRoute>
            {userHasRole(
              [UserRoles.MANAGER, UserRoles.ADMIN, UserRoles.BUSINESSUSER],
              (user?.groups || []) as UserRoles[]
            ) ? (
              <DocAddPage defaultValues={docEditDefaultValues} />
            ) : (
              <Outlet />
            )}
          </SecuredRoute>
        }
      />
      <Route
        path="/forms/:id"
        element={
          <SecuredRoute>
            {userHasRole(
              [UserRoles.MANAGER, UserRoles.ADMIN, UserRoles.BUSINESSUSER],
              (user?.groups || []) as UserRoles[]
            ) ? (
              <DocEditPage defaultValues={docEditDefaultValues} />
            ) : (
              <Outlet />
            )}
          </SecuredRoute>
        }
      />
      <Route
        path="/forms"
        element={
          <SecuredRoute>
            {userHasRole(
              [UserRoles.MANAGER, UserRoles.ADMIN],
              (user?.groups || []) as UserRoles[]
            ) ? (
              <FormList />
            ) : (
              <Outlet />
            )}
          </SecuredRoute>
        }
      />
      <Route
        path="/link"
        element={
          <SecuredRoute>
            {userHasRole(
              [UserRoles.MANAGER, UserRoles.ADMIN, UserRoles.BUSINESSUSER],
              (user?.groups || []) as UserRoles[]
            ) ? (
              <LinkLoader />
            ) : (
              <Outlet />
            )}
          </SecuredRoute>
        }
      />
      <Route
        path="/admin/add-org-hierarchy"
        element={
          <SecuredRoute>
            {userHasRole([UserRoles.ADMIN], (user?.groups || []) as UserRoles[]) ? (
              <AddOrgHierarchy />
            ) : (
              <Outlet />
            )}
          </SecuredRoute>
        }
      />
      <Route
        path="/admin/archive-org-hierarchy"
        element={
          <SecuredRoute>
            {userHasRole([UserRoles.ADMIN], (user?.groups || []) as UserRoles[]) ? (
              <ArchiveOrgHierarchy />
            ) : (
              <Outlet />
            )}
          </SecuredRoute>
        }
      />
      <Route
        path="/admin/dictionary"
        element={
          <SecuredRoute>
            {userHasRole([UserRoles.ADMIN], (user?.groups || []) as UserRoles[]) ? (
              <Dictionary />
            ) : (
              <Outlet />
            )}
          </SecuredRoute>
        }
      />
      <Route
        path="/document-api/merged-documents/file"
        element={
          <SecuredRoute>
            <DocApiDownload />
          </SecuredRoute>
        }
      />
      <Route
        path="/document-api/merged-documents/preview"
        element={
          <SecuredRoute>
            <DocApiPreview />
          </SecuredRoute>
        }
      />
      <Route
        path="/document-api/merged-documents/zip"
        element={
          <SecuredRoute>
            <DocApiZip />
          </SecuredRoute>
        }
      />
      <Route
        path="/export-metadata"
        element={
          <SecuredRoute>
            <MetadataExport />
          </SecuredRoute>
        }
      />
      <Route path="/implicit/callback" element={<CustomLoginCallback />} />
      <Route path="/" element={<AuthMain />} />
      <Route path="/auth" element={<AuthMain />} />
      <Route path="/auth-refresh" element={<AuthTokenRefresh />} />
      <Route path="/logout" element={<AuthLogout />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};
